import { Avatar, Box, Button, Card, CardContent, CircularProgress, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { ToastOptions, toast } from 'react-toastify';
import { epcClient } from '../../api/client';
import QrScan from '../../component/qrScan/QrScan';
import useAppDispatch from '../../hooks/useAppDispatch';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import { getAssignment, lookupAssignmentByItem, takePossession } from '../../redux/slices/assignments/assignmentsSlice';
import CategoryCountWidget from './CategoryCountWidget';

const toastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'dark',
} as ToastOptions;

const errorText = (rejection: unknown): string => {
  const messages = (rejection as { messages?: string[] })?.messages;
  const joined = Array.isArray(messages) ? messages.join(' ') : '';
  if (joined.includes('Not in a prepared assignment')) {
    return 'This item is not in a prepared assignment.';
  }
  if (joined.includes('Item not found')) {
    return 'Item not found.';
  }
  return 'Unable to look up the scanned item. Please try again.';
};

export const Pickup = (): ReactElement => {
  const term = useMarketTerm();
  const dispatch = useAppDispatch();
  const [showQr, setShowQr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignmentId, setAssignmentId] = useState<string | undefined>();
  const assignment = useSelector(getAssignment(assignmentId));
  const [possessing, setPossessing] = useState(false);
  const [possessionResult, setPossessionResult] = useState<string | null>(null);
  const [possessionError, setPossessionError] = useState<string | null>(null);

  const handleTakePossession = async () => {
    if (!assignmentId) return;
    setPossessing(true);
    setPossessionError(null);
    try {
      const result = await dispatch(takePossession({ assignmentId, options: { hideError: true } })).unwrap();
      const itemsTaken = result.data.result?.items_taken ?? 0;
      setPossessionResult(`In possession — ${itemsTaken} item${itemsTaken === 1 ? '' : 's'} now tracked`);
    } catch (rejection) {
      if ((rejection as { status?: number })?.status === 409) {
        setPossessionResult('Already in your possession');
      } else {
        setPossessionError('Unable to take possession. Please try again.');
      }
    } finally {
      setPossessing(false);
    }
  };

  const checkScanResult = async (decodedText: string) => {
    try {
      const response = await epcClient.validateEpc(decodedText);
      if (!response.data.result && decodedText.length >= 12) {
        toast.error(`"${decodedText}" is Invalid EPC.`, toastOptions);
        return;
      }
    } catch {
      // Format validation is best-effort; the lookup below authoritatively rejects unknown identifiers.
    }

    const identifier = decodedText.replace(/^0[xX]/, '');
    setLoading(true);
    setError(null);
    setAssignmentId(undefined);
    setPossessionResult(null);
    setPossessionError(null);
    try {
      const result = await dispatch(lookupAssignmentByItem({ identifier, options: { hideError: true } })).unwrap();
      setAssignmentId(result.data.result?.id);
    } catch (rejection) {
      setError(errorText(rejection));
    } finally {
      setLoading(false);
    }
  };

  const deliveredLabel = assignment?.delivered
    ? `Delivered${assignment.delivered_at ? ` ${DateTime.fromISO(assignment.delivered_at).toLocaleString(DateTime.DATETIME_SHORT)}` : ''}`
    : 'Prepared';

  return (
    <>
      <Helmet title="Pickup" />
      <Stack spacing={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h4">Pickup</Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowQr(true)}
            disabled={loading}
            startIcon={<Box component="i" className="tags-icon-barcode" />}
            sx={{ minHeight: 48 }}
          >
            {assignment || error ? 'Scan Another' : 'Scan Item'}
          </Button>
        </Box>

        {!assignment && !error && !loading && <Typography color="text.secondary">Scan one item to load all equipment assigned to its job.</Typography>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Card>
            <CardContent>
              <Typography color="error" gutterBottom>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check the tag and scan again.
              </Typography>
            </CardContent>
          </Card>
        )}

        {assignment && !loading && (
          <>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {assignment.job_site_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[assignment.job_site_address, assignment.job_site_city, assignment.job_site_state, assignment.job_site_postal].filter(Boolean).join(', ')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {deliveredLabel}
                </Typography>
                {assignment.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {assignment.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <CategoryCountWidget items={assignment.items} title={term('Left Onsite')} />

            <Card>
              <List dense>
                {(assignment.items ?? []).map((item) => (
                  <ListItem key={item.item_id}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={item.image_src || undefined}>
                        <Box component="i" className="tags-icon-barcode" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={item.product_name} secondary={[item.manufacturer_name, item.identifier].filter(Boolean).join(' — ')} />
                  </ListItem>
                ))}
              </List>
            </Card>

            <Box>
              {possessionResult ? (
                <Card>
                  <CardContent>
                    <Typography color="success.main">{possessionResult}</Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Button fullWidth variant="contained" size="large" sx={{ minHeight: 48 }} disabled={possessing} onClick={handleTakePossession}>
                    {possessing ? 'Taking Possession…' : 'Take Possession'}
                  </Button>
                  {possessionError && (
                    <Typography color="error" sx={{ mt: 2 }}>
                      {possessionError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Stack>

      <QrScan open={showQr} onClose={() => setShowQr(false)} checkScanResult={checkScanResult} />
    </>
  );
};

export default Pickup;
